import { Container } from "@eworkit/iris";
import React, { useContext, FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { UserContext } from "@/components/User";
import { useInfiniteQuery } from "@/core/queries/useInfiniteQuery";
import { fetchJobRequestList } from "@/data/job-requests";
import { useRouter } from "@/router";

import { DashboardJobsGridRenderer, DashboardJobsSectionHeader } from "../components";

export enum DashboardJobsTab {
  RECOMMENDED = "recommended",
  SAVED = "saved",
}

interface DashboardJobsSectionProps {
  recommendedJobsCount?: number;
  savedJobsCount?: number;
}

export const DashboardJobsSection: FC<DashboardJobsSectionProps> = ({ recommendedJobsCount, savedJobsCount }) => {
  const { t } = useTranslation("session");
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;

  const [currentTab, setCurrentTab] = useState<DashboardJobsTab | undefined>();
  const reactRouter = useRouter<{}, Record<string, string>>();

  const paramValue = reactRouter.searchParams["tab"];

  const defaultTab =
    (recommendedJobsCount && recommendedJobsCount > 0) || !savedJobsCount
      ? DashboardJobsTab.RECOMMENDED
      : DashboardJobsTab.SAVED;

  useEffect(() => {
    setCurrentTab((paramValue as DashboardJobsTab) || defaultTab);
  }, [defaultTab, paramValue]);

  const recommendedJobsQuery = useInfiniteQuery(
    ["recommended-jobs", companyId],
    fetchJobRequestList,
    {
      size: 16,
      companyId,
      filtersConfig: {
        recommendedOnly: true,
      },
    },
    {
      enabled: currentTab === DashboardJobsTab.RECOMMENDED,
      suspense: false,
    },
  );

  const savedJobsQuery = useInfiniteQuery(
    ["saved-jobs", companyId],
    fetchJobRequestList,
    {
      size: 16,
      companyId,
      filtersConfig: {
        favouritesOnly: true,
      },
    },
    { enabled: currentTab === DashboardJobsTab.SAVED },
  );

  return (
    <Container sx={{ gridColumn: "1 / 5" }}>
      <DashboardJobsSectionHeader
        recommendedJobsCount={recommendedJobsCount}
        savedJobsCount={savedJobsCount}
        currentTab={currentTab}
      />
      {currentTab === DashboardJobsTab.RECOMMENDED && (
        <DashboardJobsGridRenderer
          query={recommendedJobsQuery}
          emptyHeader={t("DASHBOARD.JOBS_SECTION.RECOMMENDED.EMPTY_HEADER")}
          emptySubheader={t("DASHBOARD.JOBS_SECTION.RECOMMENDED.EMPTY_SUBHEADER")}
        />
      )}

      {currentTab === DashboardJobsTab.SAVED && (
        <DashboardJobsGridRenderer
          query={savedJobsQuery}
          emptyHeader={t("DASHBOARD.JOBS_SECTION.SAVED.EMPTY_HEADER")}
          emptySubheader={t("DASHBOARD.JOBS_SECTION.SAVED.EMPTY_SUBHEADER")}
        />
      )}
    </Container>
  );
};
